import type { HandleImportClickParams } from "@/src/hooks/useHandleImportClick";
import { isPlaidOAuthRedirect } from "./plaidOAuth";

const STORAGE_KEY = "plaid_connect_chain_pending_v1";

/** Serializable subset of HandleImportClickParams for OAuth redirect resume. */
export type PlaidConnectChainPendingPayload = Pick<
  HandleImportClickParams,
  | "requireAccountsConfirm"
  | "accountTarget"
  | "accountType"
  | "fromOnboarding"
  | "showAddManually"
  | "hasSelectedPortfolio"
  | "initialBrokerId"
  | "accountId"
  | "connectionId"
  | "analyticsSource"
  | "trackingModuleName"
>;

let oauthResumeInFlight = false;

export const savePlaidConnectChainPending = (
  params: HandleImportClickParams,
): void => {
  if (typeof window === "undefined" || !params.requireAccountsConfirm) {
    return;
  }

  const payload: PlaidConnectChainPendingPayload = {
    requireAccountsConfirm: true,
    accountTarget: params.accountTarget,
    accountType: params.accountType,
    fromOnboarding: params.fromOnboarding,
    showAddManually: params.showAddManually,
    hasSelectedPortfolio: params.hasSelectedPortfolio,
    initialBrokerId: params.initialBrokerId,
    accountId: params.accountId,
    connectionId: params.connectionId,
    analyticsSource: params.analyticsSource,
    trackingModuleName: params.trackingModuleName,
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Safari 隐私模式 / quota —— OAuth 回跳续链降级为可忽略
  }
};

export const readPlaidConnectChainPending =
  (): PlaidConnectChainPendingPayload | null => {
    if (typeof window === "undefined") {
      return null;
    }

    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as PlaidConnectChainPendingPayload;
      if (!parsed?.requireAccountsConfirm) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

export const clearPlaidConnectChainPending = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const shouldResumePlaidConnectChain = (): boolean =>
  isPlaidOAuthRedirect() && readPlaidConnectChainPending() !== null;

export const tryAcquirePlaidOAuthResume = (): boolean => {
  if (oauthResumeInFlight) {
    return false;
  }
  oauthResumeInFlight = true;
  return true;
};

export const releasePlaidOAuthResume = (): void => {
  oauthResumeInFlight = false;
};
